import { Grid } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import React, { Fragment } from 'react';
import styled from 'styled-components';
import {
	FieldConfiguration,
	GroupFieldConfiguration,
} from '../../../api/ConfigApi';
import { AuthData } from '../../../context/auth';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import { socotraGray2 } from '../../../data/globalStylingVariables';
import {
	FieldGroupCreateRequest,
	FieldGroupsByLocatorMap,
	FieldGroupUpdateRequest,
	FieldValuesMap,
} from '../../../types/policy';
import { StyledTitle } from '../../common';
import FieldLabel from '../../fields/FieldLabel';

interface Props {
	authData: AuthData;
	policyData: PolicyData;
	originalFieldGroups: FieldGroupsByLocatorMap;
	originalFieldValues: FieldValuesMap;
	addedFieldGroups: FieldGroupCreateRequest[];
	removedFieldGroups: string[];
	updatedFieldGroups: FieldGroupUpdateRequest[];
	fieldConfigurations: FieldConfiguration[];
}

const useStyles = makeStyles((theme) => ({
	disabledInput: {
		'& .MuiInputBase-root.Mui-disabled': {
			color: 'rgba(0, 0, 0, 0.54)',
		},
	},
}));

// for review changes endorsement
const FieldGroupChanges = (props: Props) => {
	const classes = useStyles();
	const {
		originalFieldGroups,
		originalFieldValues,
		addedFieldGroups,
		removedFieldGroups,
		updatedFieldGroups,
		fieldConfigurations,
	} = props;

	const renderGroupField = (
		config: FieldConfiguration,
		fieldValues: FieldValuesMap,
		ind: number,
	) => {
		const value = fieldValues[config.name] || '--';
		const random = Math.random();
		return (
			<Grid item key={`${config.name}-${ind}`}>
				<FormControl>
					<FieldLabel
						id={`${config.name}-${ind}-label-${random}`}
						required={false}
						title={config.title}
					/>
					<TextField
						className={classes.disabledInput}
						id={`${config.name}-${ind}-${random}`}
						value={value}
						disabled
					/>
				</FormControl>
			</Grid>
		);
	};

	const renderAddedGroupFields = (
		fieldValues: FieldValuesMap,
		groupConfig: GroupFieldConfiguration,
	) => {
		return (
			<GroupFieldWrapper>
				<Grid container spacing={1}>
					{groupConfig.fields.map((fieldConfig, ind) =>
						renderGroupField(fieldConfig, fieldValues, ind),
					)}
				</Grid>
			</GroupFieldWrapper>
		);
	};

	const renderUpdatedGroupFields = (
		fieldValues: FieldValuesMap,
		groupConfig: GroupFieldConfiguration,
	) => {
		return (
			<GroupFieldWrapper>
				{Object.keys(fieldValues).map((fieldVal, ind) => {
					const fieldConfig = groupConfig.fields.find(
						(c) => c.name === fieldVal,
					);
					return fieldConfig && renderGroupField(fieldConfig, fieldValues, ind);
				})}
			</GroupFieldWrapper>
		);
	};

	const renderAddedFieldGroups = () => {
		if (!addedFieldGroups) {
			return null;
		}
		return addedFieldGroups.map(
			(fieldGroup: FieldGroupCreateRequest, index: number) => {
				const fieldConfig = fieldConfigurations.find(
					(f) => f.name === fieldGroup.fieldName,
				);

				const key = `${fieldGroup.fieldName}-${index}`;

				return (
					<Fragment key={key}>
						{fieldConfig?.newline && <Break />}
						{renderAddedGroupFields(
							fieldGroup.fieldValues,
							fieldConfig as GroupFieldConfiguration,
						)}
					</Fragment>
				);
			},
		);
	};

	const renderUpdatedFieldGroups = () => {
		if (!updatedFieldGroups) {
			return null;
		}
		return updatedFieldGroups.map(
			(fieldGroup: FieldGroupUpdateRequest, index: number) => {
				const fieldConfig = fieldConfigurations.find(
					(f) => f.name === fieldGroup.fieldName,
				);
				const key = `${fieldGroup.fieldName}-${index}`;
				return (
					<Fragment key={key}>
						{fieldConfig?.newline && <Break />}
						{renderUpdatedGroupFields(
							fieldGroup.fieldValues,
							fieldConfig as GroupFieldConfiguration,
						)}
					</Fragment>
				);
			},
		);
	};

	const renderRemovedFieldGroups = () => {
		if (!removedFieldGroups) {
			return null;
		}
		return removedFieldGroups.map((locator: string) => {
			const fieldGroupVals =
				originalFieldGroups[locator] ?? ({} as FieldValuesMap);
			const fieldGroupName = Object.keys(originalFieldValues).find((f) =>
				originalFieldValues[f].includes(locator),
			);
			const configuration = fieldConfigurations.find(
				(c) => c.name === fieldGroupName,
			);
			return (
				<GroupFieldWrapper key={locator}>
					{(configuration as GroupFieldConfiguration)?.fields?.map(
						(fieldConfig, ind) =>
							renderGroupField(fieldConfig, fieldGroupVals, ind),
					)}
				</GroupFieldWrapper>
			);
		});
	};

	const renderHeader = (title: string) => {
		return (
			<StyledTitle style={{ marginTop: '10px', marginBottom: '10px' }}>
				{title}
			</StyledTitle>
		);
	};

	return (
		<div style={{ marginTop: '10px', marginRight: '10px' }}>
			{addedFieldGroups?.length ? renderHeader('Added Field Groups') : null}
			{renderAddedFieldGroups()}
			{updatedFieldGroups?.length ? renderHeader('Updated Field Groups') : null}
			{renderUpdatedFieldGroups()}
			{removedFieldGroups?.length ? renderHeader('Removed Field Groups') : null}
			{renderRemovedFieldGroups()}
		</div>
	);
};

const Break = styled.div`
	width: 100%;
	height: 0;
`;

const GroupFieldWrapper = styled.div`
	border: 1px dotted ${socotraGray2};
	padding: 10px;
	width: 100%;
	margin-bottom: 8px;
`;

export default withPolicyContext<Props>(FieldGroupChanges);
